/**
 * Shared worksheet types — frozen Phase 0 contract.
 *
 * Mirrors the enums in backend/gymea_edu/models/worksheet.py (referenced there
 * via the module namespace, e.g. models.worksheet.Template). The wire values
 * are the contract; enum names follow each language's idiom. See
 * docs/worksheet_contract.md.
 */

export enum WorksheetTemplate {
  LESSON_STARTER = 'lesson_starter',
  KNOWLEDGE_CHECK = 'knowledge_check',
  LESSON_RECAP = 'lesson_recap',
  LESSON_ACTIVITY = 'lesson_activity',
  HOMEWORK = 'homework',
  ASSESSMENT = 'assessment'
}

export enum Differentiation {
  SUPPORT = 'support',
  CORE = 'core',
  CHALLENGE = 'challenge'
}

export enum QuestionType {
  MULTIPLE_CHOICE = 'multiple_choice',
  SHORT_ANSWER = 'short_answer',
  LONG_ANSWER = 'long_answer',
  FILL_IN_BLANK = 'fill_in_blank',
  MATCHING = 'matching',
  TRUE_FALSE = 'true_false',
  DRAWING = 'drawing'
}

export enum WorksheetJobStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

/** Optional source document when launched from a lesson plan / unit of work. */
export interface WorksheetSource {
  type: 'lesson_plan' | 'unit_of_work';
  documentId: string;
  docBase64: string;
  lessonScope: 'whole_unit' | { lessonIndex: number };
}

/** Body of POST /api/worksheets/generate (the `formData` field). */
export interface WorksheetFormData {
  curriculum: 'nsw' | 'australian' | 'vic';
  subject: string;
  stages: string[];
  worksheetType: WorksheetTemplate;
  outcomes: string;
  additionalInfo?: string;
  questionStyles: QuestionType[];
  questionCounts: Partial<Record<QuestionType, number>>;
  differentiationLevels: Differentiation[];
  includeAnswerKey: boolean;
  source?: WorksheetSource;
}

/** Response from POST /api/worksheets/generate (202). */
export interface WorksheetJobResponse {
  status: 'accepted';
  job_id: string;
  message: string;
}

export interface WorksheetJobProgress {
  percentage: number;
  message: string;
}

/**
 * A single generated worksheet in a completed job — one per differentiation
 * level. When the answer key was requested it is appended to the end of this
 * same DOCX; there is no separate answer-key document.
 */
export interface WorksheetDocument {
  differentiationLevel: Differentiation;
  documentId: string;
  docBase64: string;
  downloadUrl: string;
}

/** Response from GET /api/worksheets/status/{job_id}. */
export interface WorksheetJobStatusResponse {
  status: WorksheetJobStatus;
  progress: WorksheetJobProgress;
  documents?: WorksheetDocument[];
  error?: { message: string };
}

/** Prefilled document passed when redirecting from a lesson plan / unit. */
export interface PrefilledWorksheetDocument {
  documentId: string;
  docBase64: string;
  source: 'lesson_plan' | 'unit_of_work';
  documentLabel?: string;
  // Forwarded from the teaching-program form so the worksheet picks up the
  // same curriculum / subject / stages framing (teacher can confirm rather
  // than re-enter). All optional — older callers may omit them.
  curriculum?: 'nsw' | 'australian' | 'vic';
  subject?: string;
  stages?: string[];
}
